import React, { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { RefreshIcon } from '../../shared/icons';
import type { SupplierDto, User } from '../../types';
import SupplierModal from './SupplierModal';
import './styles.css';

const SupplierTableRow: React.FC<{
    supplier: SupplierDto;
    onEdit: (supplier: SupplierDto) => void;
}> = ({ supplier, onEdit }) => {
    return (
        <tr>
            <td>{supplier.supplierType}</td>
            <td>{supplier.name}</td>
            <td className="actions-column">
                <div>
                    <button
                        className="btn btn-small btn-edit"
                        onClick={() => onEdit(supplier)}
                        title="Редактировать поставщика"
                    >
                        ✏️
                    </button>
                </div>
            </td>
        </tr>
    );
};

interface SupplierManagementProps {
    currentUser: User | null;
}

const SupplierManagement: React.FC<SupplierManagementProps> = ({ currentUser }) => {
    const [suppliers, setSuppliers] = useState<SupplierDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentSupplier, setCurrentSupplier] = useState<SupplierDto | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const loadSuppliers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);            

            const response = await fetch('/api/suppliers');
            if (response.ok) {
                const result = await response.json();
                setSuppliers(result.data || []);
            } else {
                const errorResult = await response.json().catch(() => ({ error: 'Network error' }));
                setError(errorResult.error || 'Ошибка при загрузке поставщиков');
            }
        } catch (err) {
            setError('Произошла ошибка при загрузке поставщиков');
            console.error('Error loading suppliers:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSuppliers();
    }, [loadSuppliers]);

    const filteredSuppliers = useMemo(() => suppliers.filter(supplier =>
        !searchTerm ||
        supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.supplierType.toString().includes(searchTerm)
    ), [suppliers, searchTerm]);

    const handleSaveSupplier = useCallback(async (supplierType: number, data: string) => {
        const response = await fetch(`/api/suppliers/${supplierType}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: data,
        });

        const result = await response.json();

        if (response.ok && result.data) {
            toast.success('Поставщик обновлен успешно!');
            loadSuppliers();
            setModalOpen(false);
            setCurrentSupplier(null);
        } else {
            toast.error('Ошибка при обновлении поставщика: ' + (result.error || 'Unknown error'));
            throw new Error(result.error || 'Unknown error');
        }
    }, [loadSuppliers]);

    const handleEditClick = useCallback((supplier: SupplierDto) => {
        setCurrentSupplier(supplier);
        setModalOpen(true);
    }, []);

    const handleModalClose = useCallback(() => {
        setModalOpen(false);
        setCurrentSupplier(null);
    }, []);

    if (error) {
        return (
            <div className="alert alert-danger">
                <h5 className="alert-heading">Ошибка</h5>
                <p>{error}</p>
                <button className="btn btn-primary" onClick={loadSuppliers}>
                    Повторить загрузку
                </button>
            </div>
        );
    }

    return (
        <div className="supplier-management container-fluid">
            <div className="filter-controls-compact">
                <div className="filter-controls-row">
                    <h3>Поставщики</h3>

                    <div className="filter-controls-right">
                        <div className="search-input-compact">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="search-icon">
                                <circle cx="11" cy="11" r="8"/>
                                <path d="m21 21-4.35-4.35"/>
                            </svg>
                            <input
                                type="text"
                                className="form-control-compact"
                                placeholder="Поиск..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    className="clear-search"
                                    onClick={() => setSearchTerm('')}
                                    title="Очистить поиск"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            )}
                        </div>

                        <button className="btn-compact refresh" onClick={loadSuppliers} disabled={loading} title="Обновить">
                            {loading ? (
                                <span className="btn-spinner"></span>
                            ) : (
                                <RefreshIcon />
                            )}
                        </button>
                    </div>
                </div>

            {searchTerm && (
                    <div className="active-filters-compact">
                        <span className="filter-tag-compact">
                            <span className="tag-text">Поиск: "{searchTerm}"</span>
                            <button
                                className="tag-remove-compact"
                                onClick={() => setSearchTerm('')}
                                title="Удалить фильтр"
                            >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </span>
                    </div>
                )}
            </div>

            <div className="supplier-management-table-container">
                <table className="supplier-management-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Название</th>
                            <th className="actions-column"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredSuppliers.map(supplier => (
                            <SupplierTableRow
                                key={supplier.supplierType}
                                supplier={supplier}
                                onEdit={handleEditClick}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredSuppliers.length === 0 && (
                <div className="text-center py-4">
                    <p className="text-muted">Поставщики не найдены</p>
                </div>
            )}

            <div className="mt-3 text-muted">
                Показано {filteredSuppliers.length} из {suppliers.length} поставщиков
            </div>

            <SupplierModal
                supplier={currentSupplier || undefined}
                currentUser={currentUser}
                onSave={handleSaveSupplier}
                onClose={handleModalClose}
                isOpen={modalOpen}
            />
        </div>
    );
};

export default SupplierManagement;
